// Pure device/local-state hook for the opt-in biometric app-lock — no API calls, so this
// lives directly under `src/hooks/` rather than `src/hooks/featues/` (reserved for
// React-Query-backed backend domains).
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth'
import { Capacitor } from '@capacitor/core'
import { useCallback, useEffect, useState } from 'react'

const BIOMETRIC_LOCK_STORAGE_KEY = 'biometricLockEnabled'
const BIOMETRIC_AVAILABLE_CACHE_KEY = 'biometricAvailableCache'

// `enabled` is read by both the Account settings toggle and `AppLockGate` (mounted once
// near the app root) — a plain module-level store + listener list keeps every hook
// instance in sync the moment the toggle changes, without adding a state library
// (mirrors the listener-list pattern already used by `src/hooks/useToast.ts`).
const enabledListeners = new Set<(enabled: boolean) => void>()
let enabledState = localStorage.getItem(BIOMETRIC_LOCK_STORAGE_KEY) === 'true'

// Last-known result of `BiometricAuth.checkBiometry()`, cached across launches the same way
// `enabledState` is. Without this, `isAvailable` starts `false` on every cold start and only
// flips once `checkBiometry()` resolves asynchronously — during that round-trip `AppLockGate`
// (which requires `isAvailable` before activating) is provably inactive, so a returning,
// already-authenticated, lock-enabled user would briefly see protected content unlocked. Using
// the cached value lets the gate activate synchronously from the first render; `checkBiometry()`
// still runs below to re-validate and correct both this cache and the live state if the
// hardware/enrollment genuinely changed. No cache yet (first-ever launch) falls back to
// `false` (fail open), same as before — unavoidable, not a regression.
let cachedIsAvailable = localStorage.getItem(BIOMETRIC_AVAILABLE_CACHE_KEY) === 'true'

function setStoredEnabled(value: boolean): void {
  enabledState = value
  localStorage.setItem(BIOMETRIC_LOCK_STORAGE_KEY, String(value))
  enabledListeners.forEach((listener) => listener(value))
}

function setCachedIsAvailable(value: boolean): void {
  cachedIsAvailable = value
  localStorage.setItem(BIOMETRIC_AVAILABLE_CACHE_KEY, String(value))
}

export function useBiometricLock() {
  const [enabled, setEnabledLocal] = useState(enabledState)
  const [isAvailable, setIsAvailable] = useState(cachedIsAvailable)
  const [locked, setLocked] = useState(false)

  useEffect(() => {
    enabledListeners.add(setEnabledLocal)
    return () => {
      enabledListeners.delete(setEnabledLocal)
    }
  }, [])

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    let cancelled = false
    BiometricAuth.checkBiometry()
      .then((result) => {
        setCachedIsAvailable(result.isAvailable)
        if (!cancelled) setIsAvailable(result.isAvailable)
      })
      .catch(() => {
        setCachedIsAvailable(false)
        if (!cancelled) setIsAvailable(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const setEnabled = useCallback((value: boolean) => {
    setStoredEnabled(value)
  }, [])

  const lock = useCallback(() => {
    setLocked(true)
  }, [])

  const verify = useCallback(async () => {
    // `allowDeviceCredential` keeps the device PIN/pattern/passcode as a fallback
    // whenever biometrics themselves are unavailable, unenrolled, or locked out —
    // without it, a user who loses biometric access after enabling the toggle would
    // have no way to unlock the app short of logging out (see AppLockGate).
    await BiometricAuth.authenticate({ allowDeviceCredential: true })
    setLocked(false)
  }, [])

  return { isAvailable, enabled, setEnabled, locked, lock, verify }
}
